import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { League } from '../models/league';
import { map } from 'rxjs/operators';
import { Tournament, TournamentStatuses } from '../models/tournament';
import { ConfigService } from '../services/config.service';
import { applyGamesFilters, TournamentEventsListFilter, TournamentGamesFilters } from './tournament-api';
import { PaginatedResponse } from './paginated-response.interface';
import { TournamentEvent, TournamentEventTypes } from '../models/tournament-event';
import { GameMediaFilters } from './media-api';
import { TournamentSeason } from '../models/tournament-season';
import { Game, GameStatuses } from '../models/game';
import { LeaguePartner } from '../models/league-partner';
import { LeagueCourt } from '../models/league-court';
import { BannerLocation, LeagueBanner } from '../models/league-banner';
import { LeagueDocument } from '../models/league-document';
import { TeamsAndUsers } from '../models/teams-and-users';
import { SeasonTournament } from '../models/season-tournament';
import { LeaguePlayerField } from '../models/league-player-field';

export enum TournamentFilterStatusEnum {
  open = 1,
  recruitment,
  in_progress,
  closed,
}

export interface TournamentListFilters {
  search?: string;
  statuses?: TournamentStatuses[];
  tournamentStatus?: TournamentFilterStatusEnum;
  seasonId?: number;
  seasonTournamentId?: number;
}

@Injectable({providedIn: 'root'})
export class LeagueApi {
  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService
  ) {
  }

  async getByAlias(alias: string): Promise<League> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${alias}/`).pipe(
      map(result => League.toFront(result))
    ).toPromise();
  }

  async getByDomain(domain: string): Promise<League> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/by_domain/${domain}/`).pipe(
      map(result => League.toFront(result))
    ).toPromise();
  }

  async getSeasonTournaments(leagueId: number): Promise<SeasonTournament[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/season_tournaments/`).pipe(
      map(response => SeasonTournament.toFront(response)),
    ).toPromise();
  }

  async getTournaments(leagueId: number, filters?: TournamentListFilters): Promise<Tournament[]> {
    let params = new HttpParams();
    if (filters) {
      params = applyTournamentListFilters(params, filters);
    }
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/tournaments/`, {params}).pipe(
      map(result => Tournament.toFront(result))
    ).toPromise();
  }

  async getTournamentsPaginated(
    leagueId: number,
    page: number,
    size: number,
    filters?: TournamentListFilters,
  ): Promise<PaginatedResponse<Tournament[]>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (filters) {
      params = applyTournamentListFilters(params, filters);
    }
    return this.httpClient.get(
      `${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/tournaments/`,
      {params, observe: 'response'}
    ).pipe(
      map(response => ({
        total: +response.headers.get('X-Page-Count'),
        data: Tournament.toFront(response.body)
      }))
    ).toPromise();
  }

  async getEvents(
    leagueId: number,
    page: number,
    size: number,
    filters?: TournamentEventsListFilter,
  ): Promise<PaginatedResponse<TournamentEvent[]>> {
    let params = new HttpParams().set('page', page.toString())
      .set('size', size.toString());
    if (filters) {
      if (filters.event) {
        params = params.set('event', TournamentEventTypes[filters.event]);
      }
      if (filters.search) {
        params = params.set('search', filters.search);
      }
      if (filters.tournamentTour) {
        params = params.set('tournament_tour', filters.tournamentTour.toString());
      }
    }
    return this.httpClient.get(
      `${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/events/`,
      {params, observe: 'response'},
    ).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: TournamentEvent.toFront(result.body)
      }))
    ).toPromise();
  }

  async getTournamentSeasons(leagueId: number): Promise<TournamentSeason[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/tournament_seasons/`).pipe(
      map(result => TournamentSeason.toFront(result))
    ).toPromise();
  }

  async getGames(leagueId: number, page: number, size: number, filters: TournamentGamesFilters): Promise<PaginatedResponse<Game[]>> {
    let params = new HttpParams().set('page', page.toString());
    if (size) {
      params = params.set('size', size.toString());
    }
    params = applyGamesFilters(filters, params);
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/games/`, {params, observe: 'response'}).pipe(
      map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: Game.toFront(result.body)
      }))
    ).toPromise();
  }

  async getSeasons(leagueId: number, page = 1, size = 20, notClosed?: boolean, include?: string[]): Promise<PaginatedResponse<TournamentSeason[]>> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('not_closed', notClosed ? '1' : '')
      .set('include', include ? include.join(',') : '');
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/tournament_seasons/`, {
      params,
      observe: 'response'
    })
      .pipe(map(result => ({
        total: +result.headers.get('X-Page-Count'),
        data: TournamentSeason.toFront(result.body)
      })))
      .toPromise();
  }

  async getPartners(leagueId: number): Promise<LeaguePartner[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/partners/`).pipe(
      map(result => LeaguePartner.toFront(result))
    ).toPromise();
  }

  async getBanners(leagueId: number, location?: BannerLocation): Promise<LeagueBanner[]> {
    const params = location ? new HttpParams().set('location', location) : new HttpParams();

    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/banners/`, {params}).pipe(
      map(result => LeagueBanner.toFront(result))
    ).toPromise();
  }

  async getCourts(leagueId: number): Promise<LeagueCourt[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/courts/`).pipe(
      map(result => LeagueCourt.toFront(result))
    ).toPromise();
  }

  async getGraphicSettings(leagueId: number): Promise<any> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/graphic_settings/`)
      .toPromise();
  }

  async getDocuments(leagueId: number): Promise<LeagueDocument[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/documents/`).pipe(
      map(result => LeagueDocument.toFront(result))
    ).toPromise();
  }

  async searchTeamsAndUsers(leagueId: number, query: string): Promise<TeamsAndUsers> {
    const params = (new HttpParams()).set('query', query);

    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/team_and_user_search/`, {params}
    ).pipe(
      map(result => TeamsAndUsers.toFront(result))
    ).toPromise();
  }

  async getLeaguePlayerFields(leagueId: number): Promise<LeaguePlayerField[]> {
    return this.httpClient.get(`${this.configService.get('apiUrl')}/api/v1/league/${leagueId}/player_fields/`).pipe(
      map(result => LeaguePlayerField.toFront(result)),
    ).toPromise();
  }
}

function applyTournamentListFilters(params: HttpParams, filters: TournamentListFilters): HttpParams {
  if (filters.search) {
    params = params.set('search', filters.search);
  }
  if (filters.statuses) {
    params = params.set('statuses', filters.statuses.map(s => TournamentStatuses[s]).join(','));
  }
  if (filters.tournamentStatus) {
    params = params.set('tournament_status', TournamentFilterStatusEnum[filters.tournamentStatus]);
  }
  if (filters.seasonId) {
    params = params.set('season_id', filters.seasonId);
  }
  if (filters.seasonTournamentId) {
    params = params.set('season_tournament_id', filters.seasonTournamentId);
  }
  return params;
}
